import "react-tooltip/dist/react-tooltip.css";

import { useNavigate } from "react-router-dom";

import type { DocumentType } from "../DocumentTypes";

interface PreviewButtonProps {
  document: DocumentType;
  tooltipId?: string;
  className?: string;
  useNavigateForPreview?: boolean;
}

const isPreviewAvailable = (document: DocumentType) => {
  // if no preview job status, fall back logic depends on file type
  if (!document.preview_job_status) {
    // for PDFs, we can use the original proxy_url as fallback
    if (document.extension === ".pdf") {
      return !!(document.preview_proxy_url || document.proxy_url);
    }
    // for Excel files, we can use the original proxy_url since we parse client-side
    if (
      [".xlsx", ".xls", ".xlsm", ".xltx", ".xltm", ".csv"].includes(
        document.extension,
      )
    ) {
      return !!(document.preview_proxy_url || document.proxy_url);
    }
    // for other file types (like .docx), we need a generated preview
    return !!document.preview_proxy_url;
  }

  // if preview job is completed, check if we have the preview URL or s3 key
  if (document.preview_job_status === "completed") {
    return !!(document.preview_proxy_url || document.preview_s3_key);
  }

  // for any other status (queued, in progress, failed, unsupported), preview is not available
  return false;
};

const getPreviewTitle = (document: DocumentType) => {
  if (!document.preview_job_status) {
    // if no preview job status, check if preview is actually available
    if (!isPreviewAvailable(document)) {
      return "Document preview unavailable";
    }
    return "Preview";
  }

  switch (document.preview_job_status) {
    case "queued":
      return "Preview generating...";
    case "in progress":
      return "Preview generating...";
    case "failed":
      return "Preview generation failed";
    case "unsupported":
      // use the specific message from the backend if available
      return (
        document.preview_job_message ||
        "Preview not supported for this file type"
      );
    case "completed":
      return "Preview";
    default:
      return "Preview";
  }
};

const PreviewButton = ({
  document,
  tooltipId = "preview-tooltip",
  className = "",
  useNavigateForPreview = false,
}: PreviewButtonProps) => {
  const navigate = useNavigate();
  const previewAvailable = isPreviewAvailable(document);
  const previewTitle = getPreviewTitle(document);

  const handlePreviewClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!previewAvailable) return;

    const previewUrl = document.preview_proxy_url
      ? `/previews?key=${document.preview_s3_key}&url=${document.preview_proxy_url}`
      : `/previews?key=${document.s3_key}&url=${document.proxy_url}`;

    if (useNavigateForPreview) {
      navigate(previewUrl);
    } else {
      window.open(previewUrl);
    }
  };

  return (
    <div
      className={`material-icons preview action-icon ${!previewAvailable ? "disabled" : ""} ${className}`}
      data-tooltip-id={tooltipId}
      data-tooltip-content={previewTitle}
      data-tooltip-place="top"
      onClick={handlePreviewClick}
      style={{
        cursor: previewAvailable ? "pointer" : "not-allowed",
        opacity: previewAvailable ? 1 : 0.5,
      }}
    >
      preview
    </div>
  );
};

// export both the component and the utility functions
export { isPreviewAvailable, getPreviewTitle };
export default PreviewButton;
